import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, CircularProgress, Alert, Chip, Card, CardContent, Grid,
    TextField, InputAdornment, Tooltip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Warning as WarningIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Search as SearchIcon } from '@mui/icons-material';
import { daysFromStageDate, getTATStatus, getThreshold } from '../../../utils/tatRules';

const ROLE = 'ceo';

const LAB_TYPE_CFG = [
    { key: 'Calibration', color: '#1565c0', bg: '#e3f2fd' },
    { key: 'Testing',     color: '#6a1b9a', bg: '#f3e5f5' },
    { key: 'Medical',     color: '#00695c', bg: '#e0f2f1' },
    { key: 'PTP',         color: '#e65100', bg: '#fff3e0' },
    { key: 'RMP',         color: '#4e342e', bg: '#efebe9' },
];

const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? '—' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusCfg = {
    violated:  { color: '#b71c1c', bg: '#ffcdd2', label: 'Overshoot', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
    violating: { color: '#d32f2f', bg: '#ffebee', label: 'Overshoot', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
    warning:   { color: '#e65100', bg: '#fff3e0', label: 'Warning',   icon: <WarningIcon sx={{ fontSize: 14 }} /> },
    ok:        { color: '#2e7d32', bg: '#e8f5e9', label: 'On Time',   icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    resolved:  { color: '#1b5e20', bg: '#c8e6c9', label: 'Resolved',  icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
};

const TATViolation = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [doFilter, setDoFilter] = useState('');
    const [labTypeFilter, setLabTypeFilter] = useState('');
    const [cardFilter, setCardFilter] = useState('');

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setLoading(true); setError(null);
        try {
            const token = localStorage.getItem('authToken');
            const orgId = localStorage.getItem('orgId');
            const res = await fetch(`${process.env.REACT_APP_API_URL}/ceo/tat-violation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
                body: JSON.stringify({ orgId }),
            });
            const json = await res.json();
            if (!json.success) throw new Error(json.message || 'Fetch failed');
            setData(json.data || []);
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    const doOptions = useMemo(() => [...new Set(data.map(r => r.DoName).filter(Boolean))].sort(), [data]);

    // Only show applications that have exceeded CEO escalation threshold (DO days + 5)
    const escalated = data.filter(r => {
        const days = daysFromStageDate(r.StageDate);
        return days !== null && (r.IsResolved || days >= getThreshold(r, ROLE));
    });

    const filtered = escalated.filter(r => {
        const q = search.toLowerCase();
        if (q && !(
            (r.ApplicationNo || '').toLowerCase().includes(q) ||
            (r.LaboratoryName || '').toLowerCase().includes(q) ||
            (r.DoName || '').toLowerCase().includes(q)
        )) return false;
        if (doFilter && r.DoName !== doFilter) return false;
        if (labTypeFilter && (r.LabType || '').toLowerCase() !== labTypeFilter.toLowerCase()) return false;
        if (cardFilter) {
            const s = getTATStatus(daysFromStageDate(r.StageDate), r, ROLE);
            if (cardFilter === 'overshoot' && s !== 'violated' && s !== 'violating') return false;
            if (cardFilter === 'resolved' && s !== 'resolved') return false;
        }
        return true;
    });

    const overshoot = escalated.filter(r => { const s = getTATStatus(daysFromStageDate(r.StageDate), r, ROLE); return s === 'violated' || s === 'violating'; }).length;
    const resolved  = escalated.filter(r => r.IsResolved).length;

    const labTypeOvershoot = LAB_TYPE_CFG.reduce((acc, lt) => {
        acc[lt.key] = escalated.filter(r => {
            const s = getTATStatus(daysFromStageDate(r.StageDate), r, ROLE);
            return (s === 'violated' || s === 'violating') && (r.LabType || '').toLowerCase() === lt.key.toLowerCase();
        }).length;
        return acc;
    }, {});

    const showResolved = cardFilter === 'resolved';

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', mb: 1 }}>Turn Around Time Overshoot</Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                Days from StageDate. Threshold = stage DO days + 5 (CEO escalation). Per NABL TAT document.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                    { label: 'Overshoot', value: overshoot, color: '#b71c1c', bg: '#ffcdd2', key: 'overshoot', hint: 'Click to show overshoot applications' },
                    { label: 'Resolved',  value: resolved,  color: '#2e7d32', bg: '#e8f5e9', key: 'resolved',  hint: 'Click to show resolved applications' },
                ].map(item => (
                    <Grid item xs={6} sm={3} key={item.label}>
                        <Card
                            onClick={() => setCardFilter(f => f === item.key ? '' : item.key)}
                            sx={{
                                border: cardFilter === item.key ? `2px solid ${item.color}` : `1px solid ${item.color}44`,
                                background: item.bg,
                                cursor: 'pointer',
                                boxShadow: cardFilter === item.key ? `0 0 0 2px ${item.color}55` : undefined,
                                transition: 'box-shadow 0.15s, border 0.15s',
                            }}
                        >
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 1 } }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: item.color }}>{item.value}</Typography>
                                <Typography variant="caption" sx={{ color: item.color, display: 'block' }}>{item.label}</Typography>
                                <Typography variant="caption" sx={{ color: item.color, opacity: 0.7, fontSize: '0.65rem' }}>{item.hint}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Lab Type Overshoot Breakdown */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {LAB_TYPE_CFG.map(lt => (
                    <Grid item xs={6} sm={2} key={lt.key}>
                        <Card
                            onClick={() => setLabTypeFilter(f => f === lt.key ? '' : lt.key)}
                            sx={{
                                border: labTypeFilter === lt.key ? `2px solid ${lt.color}` : `1px solid ${lt.color}44`,
                                background: lt.bg,
                                cursor: 'pointer',
                                boxShadow: labTypeFilter === lt.key ? `0 0 0 2px ${lt.color}55` : undefined,
                                transition: 'box-shadow 0.15s, border 0.15s',
                            }}
                        >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1 } }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: lt.color }}>{labTypeOvershoot[lt.key]}</Typography>
                                <Typography variant="caption" sx={{ color: lt.color, display: 'block', fontWeight: 600 }}>{lt.key}</Typography>
                                <Typography variant="caption" sx={{ color: lt.color, opacity: 0.7, fontSize: '0.65rem' }}>Overshoot</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                <Grid item xs={12} sm={4} md={3}>
                    <TextField
                        size="small" fullWidth
                        placeholder="Search App No, Lab Name, DO…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    />
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>DO Name</InputLabel>
                        <Select value={doFilter} label="DO Name" onChange={e => setDoFilter(e.target.value)}>
                            <MenuItem value="">All DOs</MenuItem>
                            {doOptions.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {!loading && !error && (
                <TableContainer component={Paper} sx={{ maxHeight: 560, overflowX: 'auto' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { background: '#1a237e', color: '#fff', fontWeight: 600, fontSize: '0.77rem', whiteSpace: 'nowrap' } }}>
                                <TableCell>#</TableCell>
                                <TableCell>Application</TableCell>
                                <TableCell>Lab Type / Assessment</TableCell>
                                <TableCell>DO / JD</TableCell>
                                <TableCell>Stage Description</TableCell>
                                <TableCell>Stage Date</TableCell>
                                <TableCell sx={{ background: '#b71c1c !important' }}>Allowed Days</TableCell>
                                <TableCell sx={{ background: '#b71c1c !important' }}>Days at Stage</TableCell>
                                {showResolved && <TableCell sx={{ background: '#2e7d32 !important' }}>Reason for Overshoot</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow><TableCell colSpan={showResolved ? 9 : 8} align="center" sx={{ py: 4, color: '#888' }}>No records found</TableCell></TableRow>
                            )}
                            {filtered.map((row, i) => {
                                const days = daysFromStageDate(row.StageDate);
                                const status = getTATStatus(days, row, ROLE);
                                const cfg = status ? statusCfg[status] : null;
                                const allowed = getThreshold(row, ROLE);
                                return (
                                    <TableRow key={i} sx={{ background: cfg ? cfg.bg : 'inherit' }}>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{i + 1}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 180 }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#00024c' }}>{row.ApplicationNo || '—'}</Typography>
                                            <Tooltip title={row.AddressLine1 || ''}><span style={{ fontSize: '0.73rem' }}>{row.LaboratoryName || '—'}</span></Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{row.LabType || '—'}</Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>{row.AssessmentName || '—'}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>
                                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{row.DoName || '—'}</Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>{row.JDName || '—'}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', maxWidth: 200 }}>{row.stageDescription || row.Stage || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatDate(row.StageDate)}</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', color: '#555' }}>{allowed} days</TableCell>
                                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: cfg ? cfg.color : '#888' }}>
                                            {days !== null ? `${days} day${days !== 1 ? 's' : ''}` : '—'}
                                        </TableCell>
                                        {showResolved && (
                                            <TableCell sx={{ fontSize: '0.75rem', minWidth: 220 }}>
                                                {row.IsResolved ? (
                                                    <Box>
                                                        <Typography variant="caption" sx={{ display: 'block' }}><strong>Date:</strong> {formatDate(row.ResolvedDate)}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block' }}><strong>By:</strong> {row.resolvedbyName || '—'}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block' }}><strong>Remarks:</strong> {row.ResolvedRemarks || '—'}</Typography>
                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                            <strong>Attachment:</strong>{' '}
                                                            {row.ResolvedAttachment
                                                                ? <a href={row.ResolvedAttachment} target="_blank" rel="noreferrer" style={{ color: '#1a237e', fontWeight: 600 }}>View</a>
                                                                : '—'}
                                                        </Typography>
                                                    </Box>
                                                ) : '—'}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default TATViolation;
